import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Chip,
  Stack,
  CircularProgress,
  IconButton,
  Collapse,
  Paper,
  Fade,
  Grow,
  useTheme,
  alpha,
  useMediaQuery,
} from '@mui/material';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import CategoryIcon from '@mui/icons-material/Category';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExistingProductCategories from './ExhistingProductCategories';
import { toast } from 'react-toastify';

const ProductCategoriesDrawer = ({ open, onClose, productId }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm')); // mobile
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md')); // tablet
  // desktop default

  const drawerWidth = isXs ? '100vw' : isSm ? 400 : 500;

  const [categoryData, setCategoryData] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedTypes, setExpandedTypes] = useState({});

  useEffect(() => {
    if (!open) return;

    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/v1/categories/get-categories-all-list');
        const data = response.data.data || [];
        setCategoryData(data);

        // Initialize collapse state to open for all
        const initialExpanded = {};
        data.forEach((type) => {
          initialExpanded[type.type_name] = true;
        });
        setExpandedTypes(initialExpanded);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
    setSelectedCategories([]);
  }, [open]);

  const handleCheckboxChange = (checked, item, type_name) => {
    const exists = selectedCategories.find((cat) => cat.id === item.id);
    if (checked && !exists) {
      setSelectedCategories((prev) => [...prev, { ...item, type_name }]);
    } else if (!checked && exists) {
      setSelectedCategories((prev) => prev.filter((cat) => cat.id !== item.id));
    }
  };

  const handleToggleCollapse = (typeName) => {
    setExpandedTypes((prev) => ({
      ...prev,
      [typeName]: !prev[typeName],
    }));
  };

  const handleSave = async () => {
    if (selectedCategories.length === 0) return;

    setSaving(true);
    try {
      console.log(`Saving categories for product ${productId}:`, selectedCategories);
      const requests = selectedCategories.map((cat) =>
        console.log(`Saving category ${cat.id} for product ${productId}`) ||
        axios.post('/api/v1/product-categories/create', {
          productId,
          categoryId: cat.category_id,
        })
      );
      await Promise.all(requests);
      setSelectedCategories([]);
      setRefreshKey((prev) => prev + 1);
      toast.success('Categories saved successfully!');
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.message || 'Failed to save categories');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: drawerWidth,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
          backdropFilter: 'blur(10px)',
          boxShadow: theme.shadows[24],
        },
      }}
    >
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)} 0%, ${alpha(theme.palette.grey[100], 0.9)} 100%)`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: 0,
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <CategoryIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
            <Typography
              variant="h5"
              fontWeight="700"
              sx={{
                color: '#000000',
                letterSpacing: '0.5px',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              Assign Categories
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.grey[600],
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
                transform: 'scale(1.1)',
                color: theme.palette.grey[800],
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Paper>

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: { xs: 2, sm: 2.5, md: 3 },
            overflowX: 'hidden',
          }}
        >
          {loading ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              my={6}
            >
              <CircularProgress
                size={48}
                thickness={4}
                sx={{
                  color: theme.palette.primary.main,
                  mb: 2,
                }}
              />
              <Typography variant="body2" color="textSecondary">
                Loading categories...
              </Typography>
            </Box>
          ) : (
            <Fade in={!loading} timeout={500}>
              <Box>
                {categoryData.map((type, index) => (
                  <Grow key={type.id} in timeout={300 + index * 100}>
                    <Paper
                      elevation={2}
                      sx={{
                        mb: 3,
                        borderRadius: 3,
                        overflow: 'hidden',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          boxShadow: theme.shadows[6],
                          transform: 'translateY(-3px)',
                        },
                      }}
                    >
                      {/* Category Type Header */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => handleToggleCollapse(type.type_name)}
                        sx={{
                          cursor: 'pointer',
                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                          px: { xs: 2, sm: 3 },
                          py: 2.5,
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                          },
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.primary.main,
                            }}
                          />
                          <Typography
                            variant="h6"
                            fontWeight="700"
                            sx={{
                              color: '#000000',
                              fontSize: { xs: '1rem', sm: '1.1rem' },
                              letterSpacing: '0.3px',
                              textTransform: 'capitalize',
                            }}
                          >
                            {type.type_name}
                          </Typography>
                          <Chip
                            label={`${type.category_names.length} items`}
                            size="small"
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.2),
                              color: theme.palette.primary.dark,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          sx={{
                            transition: 'transform 0.3s ease-in-out',
                            transform: expandedTypes[type.type_name]
                              ? 'rotate(0deg)'
                              : 'rotate(180deg)',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.2),
                            },
                          }}
                        >
                          {expandedTypes[type.type_name] ? <ExpandLess sx={{ color: theme.palette.primary.main }} /> : <ExpandLess sx={{ transform: 'rotate(180deg)', color: theme.palette.primary.main }} />}
                        </IconButton>
                      </Box>

                      {/* Category Items */}
                      <Collapse in={expandedTypes[type.type_name]} timeout="auto" unmountOnExit>
                        <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: alpha(theme.palette.grey[50], 0.5) }}>
                          <Box
                            display="grid"
                            gridTemplateColumns={{
                              xs: '1fr',
                              sm: '1fr 1fr',
                            }}
                            gap={2}
                          >
                            {type.category_names.map((item, itemIndex) => {
                              const isChecked = selectedCategories.some((cat) => cat.id === item.id);
                              return (
                                <Grow key={item.id} in={expandedTypes[type.type_name]} timeout={200 + itemIndex * 50}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      borderRadius: 2,
                                      border: `1px solid ${isChecked ? theme.palette.primary.main : alpha(theme.palette.grey[300], 0.8)}`,
                                      backgroundColor: isChecked
                                        ? alpha(theme.palette.primary.main, 0.08)
                                        : 'white',
                                      transition: 'all 0.2s ease-in-out',
                                      '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                        transform: 'scale(1.02)',
                                      },
                                    }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={isChecked}
                                          onChange={(e) =>
                                            handleCheckboxChange(e.target.checked, item, type.type_name)
                                          }
                                          sx={{
                                            '&.Mui-checked': {
                                              color: theme.palette.primary.main,
                                            },
                                            '&:hover': {
                                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                            },
                                          }}
                                        />
                                      }
                                      label={
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: isChecked ? 600 : 500,
                                            color: isChecked ? theme.palette.primary.dark : theme.palette.text.primary,
                                            fontSize: '0.9rem',
                                            lineHeight: 1.4,
                                          }}
                                        >
                                          {item.value}
                                        </Typography>
                                      }
                                      sx={{
                                        width: '100%',
                                        margin: 0,
                                        px: 2,
                                        py: 1.5,
                                        borderRadius: 2,
                                        '& .MuiFormControlLabel-label': {
                                          width: '100%',
                                          wordWrap: 'break-word',
                                        },
                                      }}
                                    />
                                  </Paper>
                                </Grow>
                              );
                            })}
                          </Box>
                        </Box>
                      </Collapse>
                    </Paper>
                  </Grow>
                ))}

                {/* Selected Categories */}
                {selectedCategories.length > 0 && (
                  <Fade in={selectedCategories.length > 0} timeout={300}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 3,
                        mt: 4,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.04)} 100%)`,
                        border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                        <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                        <Typography
                          variant="h6"
                          fontWeight="700"
                          sx={{
                            color: '#000000',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                          }}
                        >
                          Selected Categories ({selectedCategories.length})
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1.5}>
                        {selectedCategories.map((cat, index) => (
                          <Grow key={cat.id} in timeout={200 + index * 50}>
                            <Chip
                              label={`${cat.type_name}: ${cat.value}`}
                              color="success"
                              variant="filled"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                px: 1,
                                py: 0.5,
                                '&:hover': {
                                  backgroundColor: theme.palette.success.dark,
                                  transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            />
                          </Grow>
                        ))}
                      </Stack>
                    </Paper>
                  </Fade>
                )}

                {/* Save Button */}
                <Box sx={{ mt: 4, mb: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || selectedCategories.length === 0}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 700,
                      textTransform: 'none',
                      background:
                        selectedCategories.length > 0
                          ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                          : undefined,
                      boxShadow: selectedCategories.length > 0 ? theme.shadows[6] : undefined,
                      '&:hover': {
                        boxShadow: selectedCategories.length > 0 ? theme.shadows[10] : undefined,
                        transform: selectedCategories.length > 0 ? 'translateY(-3px)' : undefined,
                      },
                      '&:disabled': {
                        backgroundColor: alpha(theme.palette.action.disabled, 0.12),
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    {saving
                      ? 'Saving Categories...'
                      : `Save ${selectedCategories.length} Selection${selectedCategories.length !== 1 ? 's' : ''}`}
                  </Button>
                </Box>

                {/* Existing Product Categories */}
                <Box sx={{ mt: 2, p: { xs: 1, sm: 2 } }}>
                  <ExistingProductCategories productId={productId} refreshTrigger={refreshKey} />
                </Box>
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ProductCategoriesDrawer;
